import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';
import ClearDataModal from '../../components/ClearDataModal';
import {
  ImageUploadModal,
  PrintModal,
  RegisterStudentModal,
  UpdateStudentModal,
  ResetPasswordModal,
  ResetParentPasswordModal,
} from './Students';

// ---------------------------------------------------------------------------
// Colour: chrome (cards/text) matches the rest of the admin panel; the data
// marks (bars/line/meter/status) use the validated reference palette from the
// dataviz skill — sequential blue for magnitude, the fixed status set for
// good/warning/critical states. Never mixed: text never wears a data colour.
// ---------------------------------------------------------------------------
const DATA = {
  seriesBlue: '#2a78d6',
  seriesBlueTint: 'rgba(42, 120, 214, 0.12)',
  seriesBlueWash: 'rgba(42, 120, 214, 0.10)',
  good: '#0ca30c',
  warning: '#fab219',
  critical: '#d03b3b',
};

function statusFor(pct) {
  if (pct >= 80) return { key: 'good', label: 'Good', color: DATA.good };
  if (pct >= 60) return { key: 'warning', label: 'Watch', color: DATA.warning };
  return { key: 'critical', label: 'Critical', color: DATA.critical };
}

// ---------------------------------------------------------------------------
// Small, self-contained helpers ported from StudentDetailsModal.jsx so the
// grade colour logic stays identical to the rest of the app.
// ---------------------------------------------------------------------------
function parseLocalDate(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    return new Date(y, m - 1, d);
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const [datePart] = trimmed.split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDisplayDate(value) {
  const parsed = parseLocalDate(value);
  if (!parsed) return '—';
  return parsed.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
}

// For timestamps (createdAt / updatedAt): date plus time, in the viewer's zone.
function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { year: 'numeric', month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true });
}

function getRegistrationStatusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' };
  if (s === 'inactivated' || s === 'inactivating') return { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' };
  if (!s) return { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' };
  return { bg: '#fffbeb', color: '#b45309', border: '#fde68a' };
}

function formatMonthLabel(monthKey) {
  const [y, m] = String(monthKey || '').split('-').map(Number);
  if (!y || !m) return monthKey || '';
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function getBatchStatusTone(status) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'completed') return { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' };
  if (normalized === 'ongoing') return { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
  if (normalized === 'pending' || normalized === 'planning') return { bg: '#fffbeb', color: '#b45309', border: '#fde68a' };
  return { bg: '#f9fafb', color: '#4b5563', border: '#e5e7eb' };
}

const hndMarkMeta = {
  D: { color: DATA.good, label: 'D', description: 'Distinction' },
  M: { color: DATA.good, label: 'M', description: 'Merit' },
  P: { color: DATA.good, label: 'P', description: 'Pass' },
  R: { color: DATA.critical, label: 'R', description: 'Referred' },
  SN: { color: DATA.warning, label: 'SN', description: 'Submitted · Not Marked' },
  NS: { color: '#9ca3af', label: 'NS', description: 'Not Submitted' },
};

function getHndGrade(marks, submissionStatus) {
  const status = (submissionStatus || '').toLowerCase();
  let code = (marks || '').toString().trim().toUpperCase();
  if (!code) code = status && status !== 'not submitted' ? 'SN' : 'NS';
  return hndMarkMeta[code] || hndMarkMeta.NS;
}

function getTopupGrade(marks, percentageRate, submissionStatus) {
  const status = (submissionStatus || '').toLowerCase();
  const rawMarks = typeof marks === 'number' ? marks : parseFloat(marks);
  const rawPct = typeof percentageRate === 'number' ? percentageRate : parseFloat(percentageRate);
  const value = !Number.isNaN(rawMarks) ? rawMarks : (!Number.isNaN(rawPct) ? rawPct : null);
  const isNotSubmitted = status === 'not submitted';
  const isSubmittedNoMarks = !isNotSubmitted && (value === null || Number.isNaN(value));

  if (isNotSubmitted) return { color: '#9ca3af', label: 'NS', description: 'Not Submitted' };
  if (isSubmittedNoMarks) return { color: DATA.warning, label: 'SN', description: 'Submitted · Not Marked' };
  if (value !== null && !Number.isNaN(value)) {
    const rounded = Math.round(value);
    return {
      color: rounded >= 40 ? DATA.good : DATA.critical,
      label: String(rounded),
      description: rounded >= 40 ? 'Pass' : 'Repeat / Fail',
    };
  }
  return { color: '#9ca3af', label: 'NS', description: 'Not Submitted' };
}

function getSubjectGrade(subjectRow, courseName) {
  const name = (courseName || '').toLowerCase();
  const isTopup = name.includes('topup') || name.includes('top-up') || name.includes('top up');
  const isHnd = name.includes('hnd');
  if (isTopup) return getTopupGrade(subjectRow?.marks, subjectRow?.percentageRate, subjectRow?.submissionStatus);
  if (isHnd) return getHndGrade(subjectRow?.marks, subjectRow?.submissionStatus);
  return getHndGrade(subjectRow?.marks, subjectRow?.submissionStatus);
}

// ---- trend chart geometry (fixed viewBox coordinate space) ----
const CHART_W = 600;
const CHART_H = 220;
const PAD = { top: 14, right: 16, bottom: 26, left: 34 };
const PLOT_W = CHART_W - PAD.left - PAD.right;
const PLOT_H = CHART_H - PAD.top - PAD.bottom;

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1180px;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  color: #6366f1;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  width: fit-content;
  &:hover { text-decoration: underline; }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
`;

const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px 0;
`;

const CardSubtitle = styled.p`
  font-size: 12.5px;
  color: #9ca3af;
  margin: 0 0 16px 0;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  flex-wrap: wrap;
`;

const ActionToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f3f4f6;
`;

const ACTION_BUTTON_TONES = {
  danger: { border: '#fecaca', bg: '#fef2f2', color: '#dc2626', hoverBg: '#fee2e2' },
  rose: { border: '#fecdd3', bg: '#fff1f2', color: '#e11d48', hoverBg: '#ffe4e6' },
  amber: { border: '#fde68a', bg: '#fffbeb', color: '#b45309', hoverBg: '#fef3c7' },
};

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${(p) => ACTION_BUTTON_TONES[p.$tone || (p.$danger ? 'danger' : '')]?.border || '#e5e7eb'};
  background: ${(p) => ACTION_BUTTON_TONES[p.$tone || (p.$danger ? 'danger' : '')]?.bg || '#fff'};
  color: ${(p) => ACTION_BUTTON_TONES[p.$tone || (p.$danger ? 'danger' : '')]?.color || '#374151'};
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: ${(p) => ACTION_BUTTON_TONES[p.$tone || (p.$danger ? 'danger' : '')]?.hoverBg || '#f9fafb'}; }

  span { display: none; }
  @media (min-width: 700px) {
    span { display: inline; }
  }
`;

const Avatar = styled.div`
  height: 68px;
  width: 68px;
  border-radius: 9999px;
  flex-shrink: 0;
  background: #e0e7ff;
  color: #4338ca;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img { height: 100%; width: 100%; object-fit: cover; }
`;

const IdentityBlock = styled.div`
  flex: 1;
  min-width: 220px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const StudentName = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border || '#e5e7eb'};
  background: ${(p) => p.$bg || '#f9fafb'};
  color: ${(p) => p.$color || '#4b5563'};
  white-space: nowrap;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 24px;
  margin-top: 14px;
`;

const ContactItem = styled.div`
  min-width: 0;
`;

const ContactLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ContactValue = styled.div`
  font-size: 13.5px;
  color: #111827;
  margin-top: 2px;
  overflow-wrap: break-word;
`;

const CourseTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const CourseTab = styled.button`
  border: 1px solid ${(p) => (p.$active ? '#6366f1' : '#e5e7eb')};
  background: ${(p) => (p.$active ? '#eef2ff' : '#fff')};
  color: ${(p) => (p.$active ? '#4338ca' : '#4b5563')};
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
`;

const ViewDetailsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #6366f1;
  background: #fff;
  color: #6366f1;
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #eef2ff; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 80;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  width: min(560px, 100%);
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px -10px rgba(0,0,0,0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid #f3f4f6;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 12.5px;
  color: #9ca3af;
  margin: 4px 0 0 0;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  height: 28px;
  width: 28px;
  border-radius: 9999px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #e5e7eb; }
`;

const ModalBody = styled.div`
  padding: 4px 24px 20px 24px;
`;

const ModalFilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 24px 6px 24px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  &:focus-within { border-color: #6366f1; background: #fff; }
`;

const ModalFilterInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: 9px 0;
  font-size: 13px;
  outline: none;
  color: #111827;
  &::placeholder { color: #9ca3af; }
`;

const ModalDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 20px 24px;
  border-top: 1px solid #f3f4f6;
`;

const RegistrationCard = styled.div`
  padding: 18px 0;
  border-bottom: ${(p) => (p.$last ? 'none' : '1px solid #f3f4f6')};
`;

const RegistrationCardTitle = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const RegistrationCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const DangerButton = styled.button`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #dc2626;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #fee2e2; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
`;

const KpiCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
`;

const KpiLabel = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-top: 6px;
  font-variant-numeric: proportional-nums;
`;

const KpiSub = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1.4fr;
  gap: 20px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MeterTrack = styled.div`
  width: 100%;
  height: 22px;
  border-radius: 11px;
  background: ${DATA.seriesBlueTint};
  overflow: hidden;
`;

const MeterFill = styled.div`
  height: 100%;
  border-radius: 11px;
  background: ${(p) => p.$color};
  width: ${(p) => Math.max(2, Math.min(100, p.$pct))}%;
  transition: width 0.5s ease;
`;

const MeterCaption = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 10px;
`;

const MeterBig = styled.div`
  font-size: 34px;
  font-weight: 700;
  color: #111827;
`;

const ChartWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 600 / 220;
`;

const TrendTooltip = styled.div`
  position: absolute;
  transform: translate(-50%, -120%);
  background: #111827;
  color: #fff;
  font-size: 11.5px;
  line-height: 1.4;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0 10px 10px 10px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  vertical-align: middle;
`;

const InlineBarTrack = styled.div`
  width: 120px;
  height: 8px;
  border-radius: 4px 8px 8px 4px;
  background: ${DATA.seriesBlueTint};
  overflow: hidden;
`;

const InlineBarFill = styled.div`
  height: 100%;
  border-radius: 4px 8px 8px 4px;
  background: ${DATA.seriesBlue};
  width: ${(p) => Math.max(2, Math.min(100, p.$pct))}%;
`;

const EmptyState = styled.div`
  padding: 24px 8px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
`;

const LoadingState = styled.div`
  padding: 60px 8px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

const Details = styled.details`
  margin-top: 14px;
  summary {
    font-size: 12.5px;
    font-weight: 600;
    color: #6366f1;
    cursor: pointer;
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function StudentInsights() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [studentError, setStudentError] = useState(null);
  const [regIndex, setRegIndex] = useState(0);

  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [subjectWise, setSubjectWise] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [hoveredTrendIndex, setHoveredTrendIndex] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseModalFilter, setCourseModalFilter] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyByRegistration, setHistoryByRegistration] = useState({});
  const [deletableCourseIds, setDeletableCourseIds] = useState(null); // null = not loaded, 'ALL' = super admin, else array of ids
  const [coordinateCourseIds, setCoordinateCourseIds] = useState([]); // course ids (as strings) this user can view/act on
  const [deletingRegistrationId, setDeletingRegistrationId] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'print' | 'image' | 'register' | 'update' | 'clean' | null
  const [mobileRequests, setMobileRequests] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/admin/students/mobile-number-requests', { params: { studentId, status: 'all' } })
      .then((resp) => { if (!cancelled) setMobileRequests(resp.data?.requests || []); })
      .catch(() => { if (!cancelled) setMobileRequests([]); });
    return () => { cancelled = true; };
  }, [studentId]);

  const loadStudent = () => {
    let cancelled = false;
    setLoadingStudent(true);
    setStudentError(null);
    axiosInstance.get(`/admin/students/${studentId}`)
      .then((resp) => {
        if (cancelled) return;
        setStudent(resp.data?.student || null);
        setRegIndex(0);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load student:', err);
        setStudentError(err?.response?.status === 404 ? 'Student not found.' : 'Failed to load student.');
      })
      .finally(() => { if (!cancelled) setLoadingStudent(false); });
    return () => { cancelled = true; };
  };

  useEffect(() => {
    const cancel = loadStudent();
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // Same course-permission source Students.jsx uses to gate its own action
  // buttons, so "who can act on this student/registration" stays consistent
  // app-wide (super admin bypasses everything; everyone else is scoped to
  // the courses they coordinate).
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/admin/me/coordinate-courses')
      .then((resp) => {
        if (cancelled) return;
        const accessibleIds = Array.isArray(resp.data?.accessibleCourseIds) ? resp.data.accessibleCourseIds : [];
        setCoordinateCourseIds(
          accessibleIds.map((id) => Number(id)).filter(Number.isFinite).map((id) => id.toString())
        );
        if (resp.data?.isSuperAdmin) {
          setDeletableCourseIds('ALL');
          return;
        }
        const coordinateCourses = resp.data?.coordinateCourses || {};
        const ids = Object.values(coordinateCourses)
          .filter((c) => c.isDelete)
          .map((c) => Number(c.courseId));
        setDeletableCourseIds(ids);
      })
      .catch(() => { if (!cancelled) { setDeletableCourseIds([]); setCoordinateCourseIds([]); } });
    return () => { cancelled = true; };
  }, []);

  const stored = AuthService.getUser();
  const staffTypeRaw = String(stored?.superAdmin?.staff?.type || '').trim().toLowerCase();
  const isSuperAdminUser = Boolean(stored?.superAdmin?.superAdminId);
  const hasFullAccess = isSuperAdminUser || staffTypeRaw === 'admin';

  const registrations = useMemo(() => (Array.isArray(student?.registrations) ? student.registrations : []), [student]);
  const currentRegistration = registrations[regIndex] || null;
  const currentRegNo = currentRegistration?.registrationNumber;
  const canDeleteCourse = (courseId) => deletableCourseIds === 'ALL'
    || (Array.isArray(deletableCourseIds) && deletableCourseIds.includes(Number(courseId)));
  const isWithinCoordination = hasFullAccess || registrations.some(
    (reg) => reg.courseId != null && coordinateCourseIds.includes(reg.courseId.toString())
  );

  const closeActionModal = () => setActiveModal(null);
  const handleActionSuccess = () => {
    closeActionModal();
    loadStudent();
  };

  const filteredModalRegistrations = useMemo(() => {
    const term = courseModalFilter.trim().toLowerCase();
    if (!term) return registrations;
    return registrations.filter((r) => (
      String(r.registrationNumber || '').toLowerCase().includes(term)
      || String(r.batchName || '').toLowerCase().includes(term)
      || String(r.batchInternalName || '').toLowerCase().includes(term)
      || String(r.courseName || '').toLowerCase().includes(term)
      || String(r.courseShortName || '').toLowerCase().includes(term)
    ));
  }, [registrations, courseModalFilter]);

  const handleRemoveRegistration = async (registration) => {
    if (!registration?.registrationId) return;

    const result = await Swal.fire({
      title: 'Delete Registration?',
      text: 'This action cannot be undone. The registration will be permanently removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setDeletingRegistrationId(registration.registrationId);
    try {
      await axiosInstance.delete(`/admin/registrations/${registration.registrationId}`);
      setStudent((prev) => {
        if (!prev) return prev;
        const nextRegistrations = (prev.registrations || [])
          .filter((r) => r.registrationId !== registration.registrationId);
        return { ...prev, registrations: nextRegistrations };
      });
      setRegIndex(0);
      await Swal.fire({
        title: 'Deleted',
        text: 'The registration has been removed.',
        icon: 'success',
        confirmButtonColor: '#64748b',
      });
    } catch (err) {
      console.error('Failed to delete registration:', err);
      Swal.fire({
        title: 'Error',
        text: err?.response?.data?.message || 'Failed to delete the registration.',
        icon: 'error',
        confirmButtonColor: '#64748b',
      });
    } finally {
      setDeletingRegistrationId(null);
    }
  };

  const openCourseModal = () => {
    setCourseModalFilter('');
    setShowCourseModal(true);
  };

  const closeCourseModal = () => {
    setShowCourseModal(false);
    setCourseModalFilter('');
  };

  // Registration history: every registration this student has ever held
  // (any status, newest first) with the history notes recorded on each. The
  // notes come from the same endpoint the Students page uses.
  const openHistoryModal = () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryByRegistration({});
    Promise.all(registrations.map((r) => (
      axiosInstance.get(`/admin/registrations/${r.registrationId}/history`)
        .then((resp) => [r.registrationId, { entries: resp.data?.history || [], error: null }])
        .catch((err) => [r.registrationId, { entries: [], error: err?.response?.data?.message || 'Failed to load history notes.' }])
    ))).then((pairs) => {
      setHistoryByRegistration(Object.fromEntries(pairs));
      setHistoryLoading(false);
    });
  };

  const closeHistoryModal = () => setShowHistoryModal(false);

  useEffect(() => {
    if (!studentId || !currentRegNo) {
      setAttendanceSummary(null);
      setSubjectWise([]);
      setPerformance([]);
      setTrend([]);
      return;
    }
    let cancelled = false;
    setLoadingAnalytics(true);
    setAnalyticsError(null);

    Promise.all([
      axiosInstance.get(`/admin/students/${studentId}/attendance/${currentRegNo}`, { validateStatus: () => true }),
      axiosInstance.get(`/admin/students/${studentId}/attendance/${currentRegNo}/subjects`, { validateStatus: () => true }),
      axiosInstance.get(`/admin/students/${studentId}/registrations/${currentRegNo}/subject-summary`, { validateStatus: () => true }),
      axiosInstance.get(`/admin/students/${studentId}/attendance/${currentRegNo}/trend`, { validateStatus: () => true }),
    ]).then(([attRes, subjRes, perfRes, trendRes]) => {
      if (cancelled) return;
      setAttendanceSummary(attRes.status < 400 ? (attRes.data?.attendanceSummary || null) : null);
      setSubjectWise(subjRes.status < 400 ? (subjRes.data?.subjectWise || []) : []);
      setPerformance(perfRes.status < 400 ? (perfRes.data?.subjects || []) : []);
      setTrend(trendRes.status < 400 ? (trendRes.data?.trend || []) : []);
    }).catch((err) => {
      if (cancelled) return;
      console.error('Failed to load analytics:', err);
      setAnalyticsError('Some analytics could not be loaded.');
    }).finally(() => { if (!cancelled) setLoadingAnalytics(false); });

    return () => { cancelled = true; };
  }, [studentId, currentRegNo]);

  const overallPct = attendanceSummary?.attendancePercentage ?? 0;
  const overallStatus = statusFor(overallPct);

  const avgScore = useMemo(() => {
    const scored = performance
      .map((s) => {
        const raw = s.marks !== null && s.marks !== undefined && s.marks !== '' ? Number(s.marks) : Number(s.percentageRate);
        return Number.isFinite(raw) ? raw : null;
      })
      .filter((v) => v !== null);
    if (scored.length === 0) return null;
    return Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10;
  }, [performance]);

  const passCount = useMemo(() => {
    return performance.filter((s) => {
      const grade = getSubjectGrade(s, currentRegistration?.courseName);
      return grade.color === DATA.good;
    }).length;
  }, [performance, currentRegistration]);

  const sortedSubjectWise = useMemo(
    () => [...subjectWise].sort((a, b) => b.attendancePercentage - a.attendancePercentage),
    [subjectWise]
  );

  const trendPoints = useMemo(() => {
    if (!trend.length) return [];
    return trend.map((t, i) => {
      const x = trend.length === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (i / (trend.length - 1)) * PLOT_W;
      const y = PAD.top + PLOT_H - (Math.max(0, Math.min(100, t.attendancePercentage)) / 100) * PLOT_H;
      return { x, y, ...t };
    });
  }, [trend]);

  const linePath = trendPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = trendPoints.length
    ? `${linePath} L ${trendPoints[trendPoints.length - 1].x.toFixed(1)},${(PAD.top + PLOT_H).toFixed(1)} L ${trendPoints[0].x.toFixed(1)},${(PAD.top + PLOT_H).toFixed(1)} Z`
    : '';

  const gridSteps = [0, 25, 50, 75, 100];

  if (loadingStudent) {
    return (
      <DashboardLayout title="Student Insights">
        <LoadingState>Loading student…</LoadingState>
      </DashboardLayout>
    );
  }

  if (studentError || !student) {
    return (
      <DashboardLayout title="Student Insights">
        <BackLink onClick={() => navigate(-1)}>← Back</BackLink>
        <EmptyState>{studentError || 'Student not found.'}</EmptyState>
      </DashboardLayout>
    );
  }

  const statusTone = getBatchStatusTone(currentRegistration?.deliveryStatus);
  const registrationTone = getRegistrationStatusTone(currentRegistration?.registrationStatus);

  return (
    <DashboardLayout title="Student Insights">
      <Grid>
        <BackLink onClick={() => navigate(-1)}>← Back</BackLink>

        <Card>
          <ActionToolbar>
            {isWithinCoordination ? (
              <>
                <ActionButton type="button" title="Print letters" onClick={() => setActiveModal('print')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Print</span>
                </ActionButton>
                <ActionButton type="button" title="Upload photo" onClick={() => setActiveModal('image')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>Image</span>
                </ActionButton>
              </>
            ) : (
              <Pill $bg="#fffbeb" $color="#b45309" $border="#fde68a">Outside your coordination</Pill>
            )}
            <ActionButton type="button" title="Register a new course" onClick={() => setActiveModal('register')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span>Register</span>
            </ActionButton>
            <ActionButton type="button" title="View registration history" onClick={openHistoryModal} disabled={registrations.length === 0}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Registration History</span>
            </ActionButton>
            {isWithinCoordination && (
              <ActionButton type="button" title="Update student details" onClick={() => setActiveModal('update')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Update</span>
              </ActionButton>
            )}
            {isWithinCoordination && (
              <ActionButton type="button" title="Reset student password" $tone="rose" onClick={() => setActiveModal('reset-password')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
                <span>Reset Password</span>
              </ActionButton>
            )}
            {isWithinCoordination && (
              <ActionButton type="button" title="Reset parent password" $tone="amber" onClick={() => setActiveModal('reset-parent-password')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
                <span>Reset Parent Password</span>
              </ActionButton>
            )}
            {hasFullAccess && (
              <ActionButton type="button" title="Clean attendance / submission data" $danger onClick={() => setActiveModal('clean')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Clean</span>
              </ActionButton>
            )}
          </ActionToolbar>

          <ProfileHeader>
            <Avatar>
              {student.imagePath ? (
                <img src={getImageURL(student.imagePath)} alt={student.fullName} />
              ) : initials(student.fullName)}
            </Avatar>
            <IdentityBlock>
              <NameRow>
                <StudentName>{student.fullName || 'Unnamed student'}</StudentName>
                {currentRegistration && (
                  <Pill $bg={statusTone.bg} $color={statusTone.color} $border={statusTone.border}>
                    {currentRegistration.deliveryStatus ? `Delivery: ${currentRegistration.deliveryStatus}` : 'Delivery status not set'}
                  </Pill>
                )}
              </NameRow>
              <ContactGrid>
                <ContactItem>
                  <ContactLabel>Student ID</ContactLabel>
                  <ContactValue>{student.studentId}</ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Registration No.</ContactLabel>
                  <ContactValue>{currentRegistration?.registrationNumber || '—'}</ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Registration Status</ContactLabel>
                  {currentRegistration?.registrationStatus ? (
                    <div style={{ marginTop: 4 }}>
                      <Pill
                        $bg={registrationTone.bg}
                        $color={registrationTone.color}
                        $border={registrationTone.border}
                        style={{ textTransform: 'capitalize', fontSize: 12 }}
                      >
                        {currentRegistration.registrationStatus}
                      </Pill>
                    </div>
                  ) : (
                    <ContactValue>—</ContactValue>
                  )}
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Telephone</ContactLabel>
                  <ContactValue>{student.telephone || '—'}</ContactValue>
                </ContactItem>
                {mobileRequests.filter((r) => r.status !== 'rejected').length > 0 && (
                  <ContactItem>
                    <ContactLabel>Additional Mobile Numbers</ContactLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                      {mobileRequests.filter((r) => r.status !== 'rejected').map((r) => (
                        <Pill
                          key={r.requestId}
                          $bg={r.status === 'pending' ? '#fffbeb' : '#ecfdf5'}
                          $color={r.status === 'pending' ? '#b45309' : '#047857'}
                          $border={r.status === 'pending' ? '#fde68a' : '#a7f3d0'}
                        >
                          {r.mobileNumber}{r.status === 'pending' ? ' · Pending' : ''}
                        </Pill>
                      ))}
                    </div>
                  </ContactItem>
                )}
                <ContactItem>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>{student.email || '—'}</ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Address</ContactLabel>
                  <ContactValue>{student.address || '—'}</ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Registered</ContactLabel>
                  <ContactValue>{formatDisplayDate(student.createdAt)}</ContactValue>
                </ContactItem>
              </ContactGrid>

              {currentRegistration && (
                <CourseTabs>
                  {registrations.length > 1 && registrations.map((r, i) => (
                    <CourseTab key={r.registrationId || i} $active={i === regIndex} onClick={() => setRegIndex(i)}>
                      {r.courseShortName || r.courseName || `Course ${i + 1}`}
                    </CourseTab>
                  ))}
                  <ViewDetailsButton type="button" onClick={openCourseModal}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View course details
                  </ViewDetailsButton>
                </CourseTabs>
              )}
            </IdentityBlock>
          </ProfileHeader>
        </Card>

        {!currentRegistration ? (
          <Card><EmptyState>This student has no course registrations yet — no analytics to show.</EmptyState></Card>
        ) : (
          <>
            <KpiGrid>
              <KpiCard>
                <KpiLabel>Overall attendance</KpiLabel>
                <KpiValue>{loadingAnalytics ? '—' : `${overallPct}%`}</KpiValue>
                <KpiSub>{attendanceSummary ? `${attendanceSummary.attendedClasses}/${attendanceSummary.totalClasses} classes` : 'No records'}</KpiSub>
              </KpiCard>
              <KpiCard>
                <KpiLabel>Subjects enrolled</KpiLabel>
                <KpiValue>{loadingAnalytics ? '—' : performance.length}</KpiValue>
                <KpiSub>{currentRegistration.courseShortName || currentRegistration.courseName || ''}</KpiSub>
              </KpiCard>
              <KpiCard>
                <KpiLabel>Average score</KpiLabel>
                <KpiValue>{loadingAnalytics ? '—' : (avgScore !== null ? avgScore : '—')}</KpiValue>
                <KpiSub>Across marked subjects</KpiSub>
              </KpiCard>
              <KpiCard>
                <KpiLabel>Passing subjects</KpiLabel>
                <KpiValue>{loadingAnalytics ? '—' : `${passCount}/${performance.length}`}</KpiValue>
                <KpiSub>Distinction, merit or pass</KpiSub>
              </KpiCard>
            </KpiGrid>

            <TwoCol>
              <Card>
                <CardTitle>Attendance</CardTitle>
                <CardSubtitle>Ratio of classes attended for this registration</CardSubtitle>
                <MeterTrack>
                  <MeterFill $pct={overallPct} $color={overallStatus.color} />
                </MeterTrack>
                <MeterCaption>
                  <MeterBig>{overallPct}%</MeterBig>
                  <Pill $bg="transparent" $color={overallStatus.color} $border={overallStatus.color}>
                    {overallStatus.label}
                  </Pill>
                </MeterCaption>
              </Card>

              <Card>
                <CardTitle>Monthly attendance trend</CardTitle>
                <CardSubtitle>Attendance percentage by month for this registration</CardSubtitle>
                {trend.length === 0 ? (
                  <EmptyState>No dated attendance records yet.</EmptyState>
                ) : (
                  <>
                    <ChartWrap>
                      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} width="100%" height="100%">
                        {gridSteps.map((g) => {
                          const y = PAD.top + PLOT_H - (g / 100) * PLOT_H;
                          return (
                            <g key={g}>
                              <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y} y2={y} stroke="#e1e0d9" strokeWidth="1" />
                              <text x={PAD.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#898781">{g}</text>
                            </g>
                          );
                        })}
                        <path d={areaPath} fill={DATA.seriesBlue} opacity="0.10" stroke="none" />
                        <path d={linePath} fill="none" stroke={DATA.seriesBlue} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                        {trendPoints.map((p, i) => (
                          <g key={p.month}>
                            <circle
                              cx={p.x} cy={p.y} r="10" fill="transparent"
                              onMouseEnter={() => setHoveredTrendIndex(i)}
                              onMouseLeave={() => setHoveredTrendIndex((cur) => (cur === i ? null : cur))}
                              style={{ cursor: 'pointer' }}
                            />
                            <circle cx={p.x} cy={p.y} r="4" fill={DATA.seriesBlue} stroke="#fff" strokeWidth="2" pointerEvents="none" />
                          </g>
                        ))}
                        {trendPoints.length > 0 && (
                          <text
                            x={trendPoints[trendPoints.length - 1].x}
                            y={trendPoints[trendPoints.length - 1].y - 10}
                            textAnchor="end"
                            fontSize="10"
                            fontWeight="700"
                            fill="#374151"
                          >
                            {trendPoints[trendPoints.length - 1].attendancePercentage}%
                          </text>
                        )}
                        {trendPoints.map((p, i) => {
                          const showEvery = Math.ceil(trendPoints.length / 6) || 1;
                          if (i % showEvery !== 0 && i !== trendPoints.length - 1) return null;
                          return (
                            <text key={`lbl-${p.month}`} x={p.x} y={CHART_H - 6} textAnchor="middle" fontSize="9" fill="#898781">
                              {formatMonthLabel(p.month)}
                            </text>
                          );
                        })}
                      </svg>
                      {hoveredTrendIndex !== null && trendPoints[hoveredTrendIndex] && (
                        <TrendTooltip
                          style={{
                            left: `${(trendPoints[hoveredTrendIndex].x / CHART_W) * 100}%`,
                            top: `${(trendPoints[hoveredTrendIndex].y / CHART_H) * 100}%`,
                          }}
                        >
                          <strong>{formatMonthLabel(trendPoints[hoveredTrendIndex].month)}</strong><br />
                          {trendPoints[hoveredTrendIndex].attendancePercentage}% · {trendPoints[hoveredTrendIndex].attendedClasses}/{trendPoints[hoveredTrendIndex].totalClasses} classes
                        </TrendTooltip>
                      )}
                    </ChartWrap>
                    <Details>
                      <summary>View monthly data as table</summary>
                      <Table style={{ marginTop: 10 }}>
                        <thead>
                          <tr><Th>Month</Th><Th>Attended</Th><Th>Total</Th><Th>Attendance %</Th></tr>
                        </thead>
                        <tbody>
                          {trend.map((t) => (
                            <tr key={t.month}>
                              <Td>{formatMonthLabel(t.month)}</Td>
                              <Td>{t.attendedClasses}</Td>
                              <Td>{t.totalClasses}</Td>
                              <Td>{t.attendancePercentage}%</Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Details>
                  </>
                )}
              </Card>
            </TwoCol>

            <Card>
              <CardTitle>Attendance by subject</CardTitle>
              <CardSubtitle>Sorted highest to lowest for this registration</CardSubtitle>
              {sortedSubjectWise.length === 0 ? (
                <EmptyState>No subject-wise attendance records yet.</EmptyState>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Subject</Th>
                      <Th>Classes</Th>
                      <Th>Attendance</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSubjectWise.map((s) => {
                      const st = statusFor(s.attendancePercentage);
                      return (
                        <tr key={s.batchSubjectId}>
                          <Td>{s.subjectName}</Td>
                          <Td>{s.attendedClasses}/{s.totalClasses}</Td>
                          <Td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <InlineBarTrack title={`${s.attendancePercentage}%`}>
                                <InlineBarFill $pct={s.attendancePercentage} />
                              </InlineBarTrack>
                              <span style={{ fontWeight: 700, color: '#111827', minWidth: 36 }}>{s.attendancePercentage}%</span>
                            </div>
                          </Td>
                          <Td>
                            <Pill $bg="transparent" $color={st.color} $border={st.color}>{st.label}</Pill>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card>
              <CardTitle>Subject performance</CardTitle>
              <CardSubtitle>Assignment submission status and marks for this registration</CardSubtitle>
              {performance.length === 0 ? (
                <EmptyState>No subjects found for this registration.</EmptyState>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Subject</Th>
                      <Th>Semester</Th>
                      <Th>Submission</Th>
                      <Th>Grade</Th>
                      <Th>Feedback notes</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.map((s) => {
                      const grade = getSubjectGrade(s, currentRegistration?.courseName);
                      return (
                        <tr key={s.batchSubjectId}>
                          <Td>{s.subjectName}</Td>
                          <Td>{s.semester || '—'}</Td>
                          <Td style={{ textTransform: 'capitalize' }}>{s.submissionStatus || 'not submitted'}</Td>
                          <Td>
                            <Pill $bg="transparent" $color={grade.color} $border={grade.color} title={grade.description}>
                              {grade.label}
                            </Pill>
                          </Td>
                          <Td>{s.feedbackCount || 0}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card>

            {analyticsError && <EmptyState>{analyticsError}</EmptyState>}
          </>
        )}
      </Grid>

      {showCourseModal && registrations.length > 0 && (
        <ModalOverlay onClick={closeCourseModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>Registered courses</ModalTitle>
                <ModalSubtitle>{registrations.length} registration{registrations.length === 1 ? '' : 's'} for {student.fullName}</ModalSubtitle>
              </div>
              <ModalCloseButton type="button" aria-label="Close" onClick={closeCourseModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </ModalCloseButton>
            </ModalHeader>
            <ModalFilterBar>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              <ModalFilterInput
                type="text"
                placeholder="Filter by registration number, batch or course name…"
                value={courseModalFilter}
                onChange={(e) => setCourseModalFilter(e.target.value)}
                autoFocus
              />
            </ModalFilterBar>
            <ModalBody>
              {filteredModalRegistrations.length === 0 ? (
                <EmptyState>No registrations match "{courseModalFilter}".</EmptyState>
              ) : filteredModalRegistrations.map((r, i) => {
                const isDeleting = deletingRegistrationId === r.registrationId;
                return (
                  <RegistrationCard key={r.registrationId || i} $last={i === filteredModalRegistrations.length - 1}>
                    <RegistrationCardTitle>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{r.courseName || 'Course'}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{r.courseShortName || ''}</div>
                      </div>
                    </RegistrationCardTitle>
                    <ModalDetailGrid>
                      <ContactItem>
                        <ContactLabel>Batch</ContactLabel>
                        <ContactValue>{r.batchName || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>Batch internal name</ContactLabel>
                        <ContactValue>{r.batchInternalName || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>Registration number</ContactLabel>
                        <ContactValue>{r.registrationNumber || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>Registration status</ContactLabel>
                        <ContactValue style={{ textTransform: 'capitalize' }}>{r.registrationStatus || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>Delivery status</ContactLabel>
                        <ContactValue style={{ textTransform: 'capitalize' }}>{r.deliveryStatus || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>Name verification</ContactLabel>
                        <ContactValue style={{ textTransform: 'capitalize' }}>{r.nameVerification || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>Stream</ContactLabel>
                        <ContactValue>{r.stream || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>University ID</ContactLabel>
                        <ContactValue>{r.universityID || '—'}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>University register date</ContactLabel>
                        <ContactValue>{formatDisplayDate(r.universityRegisterDate)}</ContactValue>
                      </ContactItem>
                      <ContactItem>
                        <ContactLabel>WhatsApp group</ContactLabel>
                        <ContactValue>{r.whatsappGroupName || '—'}</ContactValue>
                      </ContactItem>
                    </ModalDetailGrid>
                    {canDeleteCourse(r.courseId) && (
                      <RegistrationCardFooter>
                        <DangerButton type="button" disabled={isDeleting} onClick={() => handleRemoveRegistration(r)}>
                          {isDeleting ? 'Removing…' : 'Remove course'}
                        </DangerButton>
                      </RegistrationCardFooter>
                    )}
                  </RegistrationCard>
                );
              })}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton type="button" onClick={closeCourseModal}>Close</SecondaryButton>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {showHistoryModal && (
        <ModalOverlay onClick={closeHistoryModal}>
          <ModalBox style={{ width: 'min(720px, 100%)' }} onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>Registration history</ModalTitle>
                <ModalSubtitle>
                  {registrations.length} registration{registrations.length === 1 ? '' : 's'} for {student.fullName}, newest first
                </ModalSubtitle>
              </div>
              <ModalCloseButton type="button" aria-label="Close" onClick={closeHistoryModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {historyLoading ? (
                <EmptyState>Loading history…</EmptyState>
              ) : registrations.map((r, i) => {
                const regTone = getRegistrationStatusTone(r.registrationStatus);
                const deliveryTone = getBatchStatusTone(r.deliveryStatus);
                const result = historyByRegistration[r.registrationId] || { entries: [], error: null };
                const isCurrent = currentRegistration && r.registrationId === currentRegistration.registrationId;
                return (
                  <RegistrationCard key={r.registrationId || i} $last={i === registrations.length - 1}>
                    <RegistrationCardTitle>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{r.courseName || 'Course'}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                          {r.batchInternalName || r.batchName || '—'} · Reg. no. {r.registrationNumber || '—'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
                        {isCurrent && <Pill $bg="#eef2ff" $color="#4338ca" $border="#c7d2fe">Selected</Pill>}
                        <Pill $bg={regTone.bg} $color={regTone.color} $border={regTone.border} style={{ textTransform: 'capitalize' }}>
                          {r.registrationStatus || 'No status'}
                        </Pill>
                        {r.deliveryStatus && (
                          <Pill $bg={deliveryTone.bg} $color={deliveryTone.color} $border={deliveryTone.border}>{r.deliveryStatus}</Pill>
                        )}
                      </div>
                    </RegistrationCardTitle>

                    <div style={{ fontSize: 12, color: '#6b7280', margin: '10px 0 12px', display: 'grid', gap: 3 }}>
                      <div><strong style={{ color: '#374151' }}>Registered</strong> {formatDateTime(r.createdAt)}{r.createdByName ? ` · by ${r.createdByName}` : ''}</div>
                      <div><strong style={{ color: '#374151' }}>Last updated</strong> {formatDateTime(r.updatedAt)}{r.updatedByName ? ` · by ${r.updatedByName}` : ''}</div>
                      {r.universityRegisterDate && (
                        <div><strong style={{ color: '#374151' }}>University registered</strong> {formatDisplayDate(r.universityRegisterDate)}{r.universityID ? ` · ID ${r.universityID}` : ''}</div>
                      )}
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 8 }}>
                      History notes{result.entries.length ? ` (${result.entries.length})` : ''}
                    </div>
                    {result.error ? (
                      <div style={{ fontSize: 13, color: '#b91c1c' }}>{result.error}</div>
                    ) : result.entries.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#9ca3af' }}>No history notes recorded for this registration.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 10, borderLeft: '2px solid #e5e7eb', paddingLeft: 14 }}>
                        {result.entries.map((h) => (
                          <div key={h.historyId}>
                            {h.title && <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{h.title}</div>}
                            {h.note && <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', marginTop: h.title ? 2 : 0 }}>{h.note}</div>}
                            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>
                              {formatDateTime(h.createdAt)}{h.createdByName ? ` · ${h.createdByName}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </RegistrationCard>
                );
              })}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton type="button" onClick={closeHistoryModal}>Close</SecondaryButton>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {activeModal === 'print' && student && (
        <PrintModal
          student={{ ...student, universityId: currentRegistration?.universityID || '' }}
          onClose={closeActionModal}
        />
      )}

      {activeModal === 'image' && student && (
        <ImageUploadModal student={student} onClose={closeActionModal} onSuccess={handleActionSuccess} />
      )}

      {activeModal === 'register' && student && (
        <RegisterStudentModal student={student} onClose={closeActionModal} onSuccess={handleActionSuccess} />
      )}

      {activeModal === 'update' && student && (
        <UpdateStudentModal student={student} onClose={closeActionModal} onSuccess={handleActionSuccess} />
      )}

      {activeModal === 'reset-password' && student && (
        <ResetPasswordModal student={student} onClose={closeActionModal} onSuccess={handleActionSuccess} currentUser={stored} />
      )}

      {activeModal === 'reset-parent-password' && student && (
        <ResetParentPasswordModal student={student} onClose={closeActionModal} onSuccess={handleActionSuccess} currentUser={stored} />
      )}

      {activeModal === 'clean' && student && (
        <ClearDataModal
          student={{ ...student, coordinateCourseIds }}
          onClose={closeActionModal}
          onSuccess={handleActionSuccess}
          isSuperAdmin={isSuperAdminUser}
        />
      )}
    </DashboardLayout>
  );
}
